import { useAppDispatch } from 'app/hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'hooks/useForm';
import { ChangeEvent, FormEvent, useCallback, useEffect, useState } from 'react';
import { setErrorMessage, setSuccessMessage } from 'store/toast-slice';
import { ServerErrorType } from 'types/server-responses';
import { trimValues } from 'utils/object-helpers';
import { useEditNestedProductCategoryMutation, useGetNestedProductCategoryQuery } from '../services';
import {
    NESTED_PRODUCT_CATEGORY_FIELDS_SCHEMA,
    NESTED_PRODUCT_CATEGORY_INITIAL_FIELDS,
} from '../constants/nested-product-categories-fields';
import { NESTED_PRODUCT_CATEGORY_IS_MULTI_SELECT, NESTED_PRODUCT_CATEGORY_STATUS } from '../constants/status';
import { NestedProductCategory, NestedProductCategoryDto } from '../types/dtos';

const useController = () => {
    const [isUnlimited, setIsUnlimited] = useState<boolean>();
    const [isDirtyData, setIsDirtyData] = useState<boolean>(false);
    const { nestedProductCategoryId } = useParams();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [editNestedProductCategory, { isLoading: isEditNestedProductCategoryLoading }] =
        useEditNestedProductCategoryMutation();
    const { data: nestedProductCategory } = useGetNestedProductCategoryQuery(nestedProductCategoryId as string);

    const { formData, setInitialFormData, handleChange, handleSubmit, errors, isDirty, changedValues } = useForm({
        validationSchema: NESTED_PRODUCT_CATEGORY_FIELDS_SCHEMA,
        options: {
            trackChangedValues: true,
        },
    });

    useEffect(() => {
        if (nestedProductCategory) {
            const initialCategory = {
                ...nestedProductCategory,
                isActive: nestedProductCategory.isActive
                    ? NESTED_PRODUCT_CATEGORY_STATUS.ACTIVE
                    : NESTED_PRODUCT_CATEGORY_STATUS.INACTIVE,
                isMultiSelect: nestedProductCategory.isMultiSelect
                    ? NESTED_PRODUCT_CATEGORY_IS_MULTI_SELECT.YES
                    : NESTED_PRODUCT_CATEGORY_IS_MULTI_SELECT.NO,
                maxSelection: nestedProductCategory.maxSelection === null ? '' : nestedProductCategory.maxSelection,
            };
            setInitialFormData({ ...NESTED_PRODUCT_CATEGORY_INITIAL_FIELDS, ...initialCategory });
            const unlimited =
                nestedProductCategory.maxSelection === null &&
                nestedProductCategory.isMultiSelect.toString() === 'true';
            setIsUnlimited(unlimited);
        }
    }, [nestedProductCategory, setInitialFormData]);

    const handleSuccess = useCallback(() => {
        dispatch(setSuccessMessage({ key: 'nestedProductCategory.NESTED_PRODUCT_CATEGORY_EDITED' }));
    }, [dispatch]);

    const handleErrors = useCallback(
        (error: ServerErrorType) => {
            const code = error.data?.errorCode;
            dispatch(setErrorMessage({ key: code }));
        },
        [dispatch],
    );

    const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
        setIsUnlimited(event.target.checked);
        setIsDirtyData((prev) => !prev);
    };

    const getPayload = useCallback(() => {
        const data = { ...changedValues };

        if (data.isActive) {
            data.isActive = data.isActive === NESTED_PRODUCT_CATEGORY_STATUS.ACTIVE ? true : false;
        }

        if (data.isMultiSelect) {
            data.isMultiSelect = data.isMultiSelect === NESTED_PRODUCT_CATEGORY_IS_MULTI_SELECT.YES ? true : false;
        } else {
            data.isMultiSelect = nestedProductCategory?.isMultiSelect;
        }

        if (isUnlimited || !data.isMultiSelect) {
            data.maxSelection = null;
        } else if (data.maxSelection) {
            data.maxSelection = Number(data.maxSelection);
        }

        return trimValues(data) as NestedProductCategoryDto;
    }, [changedValues, isUnlimited, nestedProductCategory]);

    const onSubmit = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            handleSubmit(async () => {
                try {
                    const payload = getPayload();

                    await editNestedProductCategory({ id: nestedProductCategoryId as string, payload }).unwrap();

                    handleSuccess();

                    navigate(-1);
                } catch (error) {
                    handleErrors(error as ServerErrorType);
                }
            });
        },
        [
            editNestedProductCategory,
            getPayload,
            handleErrors,
            handleSubmit,
            handleSuccess,
            nestedProductCategoryId,
            navigate,
        ],
    );

    const onCancel = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    return {
        formData: formData as NestedProductCategory,
        onChange: handleChange,
        onSubmit,
        onCancel,
        handleCheckboxChange,
        isUnlimited: isUnlimited ? isUnlimited : false,
        dataChanged: isDirty || isDirtyData,
        isLoading: formData === undefined,
        isEditProductCategoryLoading: isEditNestedProductCategoryLoading,
        errors,
    };
};

export default useController;
